import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Link,
} from '@mui/material';
import {
  PhoneAndroid,
  Laptop,
  Tv,
  Search as SearchIcon,
  ArrowForward as ArrowForwardIcon,
  Battery50 as BatteryIcon,
  ScreenRotation as ScreenIcon,
  BugReport as BugIcon,
  WaterDrop as WaterIcon,
  Memory as MemoryIcon,
  Storage as StorageIcon,
  Security as SecurityIcon,
  Backup as BackupIcon,
  SettingsInputComponent as ComponentIcon,
  SmartDisplay as SmartTvIcon,
  VolumeUp as VolumeIcon,
  Cable as CableIcon,
} from '@mui/icons-material';
import logo0 from '../assets/logo0.png';
import ebookIcon from '../assets/path-to-ebooks-icon.png';

const KnowledgeBase = () => {
const categories = [
    {
      title: 'Smartphone Repairs',
      icon: <PhoneAndroid />,
      description: 'Learn about common smartphone issues and their solutions',
      articles: [
        { title: 'Screen Repairs', icon: <ScreenIcon /> },
        { title: 'Battery Issues', icon: <BatteryIcon /> },
        { title: 'Software Problems', icon: <BugIcon /> },
        { title: 'Water Damage', icon: <WaterIcon /> }
      ]
    },
    {
      title: 'Computer Services',
      icon: <Laptop />,
      description: 'Comprehensive guides for computer maintenance and repairs',
      articles: [
        { title: 'Hardware Upgrades', icon: <MemoryIcon /> },
        { title: 'OS Installation', icon: <StorageIcon /> },
        { title: 'Virus Removal', icon: <SecurityIcon /> },
        { title: 'Data Recovery', icon: <BackupIcon /> }
      ]
    },
    {
      title: 'TV Solutions',
      icon: <Tv />,
      description: 'Expert tips for TV troubleshooting and maintenance',
      articles: [
        { title: 'Display Issues', icon: <ComponentIcon /> },
        { title: 'Smart TV Setup', icon: <SmartTvIcon /> },
        { title: 'Sound Problems', icon: <VolumeIcon /> },
        { title: 'Connection Guide', icon: <CableIcon /> }
      ]
    }
  ];

  return (
    <Box sx={{ bgcolor: '#000', minHeight: '100vh', color: 'white', display: 'flex', flexDirection: 'column' }}>
      {/* Logo */}
      <Box sx={{ 
        position: 'absolute', 
        top: 15, 
        left: 20, 
        zIndex: 1000,
      }}>
        <img 
          src={logo0} 
          alt="Warrili" 
          style={{ 
            height: '35px',
            filter: 'brightness(0) invert(1)',
            opacity: 0.9,
          }} 
        />
      </Box>

      {/* Enhanced Hero Section */}
      <Box sx={{
        pt: 12,
        pb: 6,
        background: 'linear-gradient(45deg, #000 30%, #111 90%)',
        borderBottom: '1px solid rgba(255,227,2,0.1)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography variant="h3" sx={{
                fontWeight: 600,
                mb: 2,
                '& span': { 
                  color: '#FFE302',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    bottom: -4,
                    width: '100%',
                    height: '2px',
                    background: 'linear-gradient(90deg, #FFE302, #4CAF50)'
                  }
                }
              }}>
                Knowledge <span>Base</span>
              </Typography>
              <Typography variant="h6" sx={{ 
                color: '#999', 
                mb: 4,
                maxWidth: '600px' 
              }}>
                Find solutions, guides, and expert tips for your device repairs
              </Typography>
              
              {/* Enhanced Search Box */}
              <Box sx={{
                display: 'flex',
                gap: 1,
                maxWidth: 500,
                bgcolor: 'rgba(255,255,255,0.05)',
                p: 0.5,
                borderRadius: 1,
                border: '1px solid rgba(255,227,2,0.1)',
                '&:focus-within': {
                  borderColor: '#4CAF50'
                }
              }}>
                <IconButton sx={{ color: '#FFE302' }}>
                  <SearchIcon />
                </IconButton>
                <input
                  placeholder="Search knowledge base..."
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'white',
                    width: '100%',
                    padding: '8px',
                    outline: 'none',
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={5} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Box sx={{
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '300px',
                  height: '300px',
                  background: 'radial-gradient(circle, rgba(76,175,80,0.1) 0%, transparent 70%)',
                  animation: 'pulse 2s infinite'
                }
              }}>
                <img
                  src={ebookIcon}
                  alt="Knowledge Base"
                  style={{
                    maxWidth: '280px',
                    filter: 'drop-shadow(0 0 20px rgba(76,175,80,0.3))',
                    animation: 'float 3s ease-in-out infinite'
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Categories Section with Green Accents */}
      <Container maxWidth="lg" sx={{ mt: 6, flex: 1 }}>
        <Grid container spacing={4}>
          {categories.map((category, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card sx={{
                bgcolor: '#111',
                color: 'white',
                height: '100%',
                border: '1px solid rgba(255,227,2,0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  borderColor: '#4CAF50',
                }
              }}>
                <CardContent>
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mb: 3,
                    mt: 2,
                  }}>
                    <Box sx={{
                      background: 'linear-gradient(135deg, #FFE302 0%, #4CAF50 100%)',
                      color: 'black',
                      borderRadius: '50%',
                      p: 3,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'rotate(360deg)',
                      }
                    }}>
                      {category.icon}
                    </Box>
                  </Box>
                  <Typography variant="h5" sx={{ 
                    mb: 2, 
                    fontWeight: 600,
                    textAlign: 'center'
                  }}>
                    {category.title}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: '#999', 
                    mb: 3,
                    textAlign: 'center'
                  }}>
                    {category.description}
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    {category.articles.map((article, i) => (
                      <Box 
                        key={i}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          mb: 2,
                          p: 1,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: 'rgba(255,227,2,0.1)',
                            color: '#FFE302',
                            transform: 'translateX(10px)',
                          }
                        }}
                      >
                        {article.icon}
                        <Typography variant="body2">{article.title}</Typography>
                      </Box>
                    ))}
                  </Box>

                  <Button
                    variant="contained"
                    fullWidth
                    sx={{
                      bgcolor: '#FFE302',
                      color: 'black',
                      '&:hover': {
                        bgcolor: '#e6cc00',
                      }
                    }}
                  >
                    View All Articles
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* New Footer */}
      <Box sx={{
        mt: 8,
        py: 3,
        bgcolor: '#111',
        borderTop: '1px solid rgba(255,255,255,0.1)',
      }}>
        <Container maxWidth="lg">
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <img 
                  src={logo0} 
                  alt="Warrili" 
                  style={{ 
                    height: '25px',
                    filter: 'brightness(0) invert(1)',
                    opacity: 0.7,
                  }} 
                />
                <Typography variant="caption" sx={{ color: '#666' }}>
                  © 2024 Warrili. All rights reserved.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
                gap: 3,
                '& a': {
                  color: '#666',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  transition: 'color 0.3s ease',
                  '&:hover': {
                    color: '#FFE302'
                  }
                }
              }}>
                <Link href="/privacy-policy">Privacy Policy</Link>
                <Link href="/terms">Terms of Service</Link>
                <Link href="/guidelines">Guidelines</Link>
                <Link href="/contact">Contact</Link>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

// Add these keyframe animations to your global CSS
const globalStyles = `
  @keyframes float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-20px); }
  }

  @keyframes pulse {
    0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.5; }
    50% { transform: translate(-50%, -50%) scale(1.1); opacity: 0.8; }
  }
`;

export default KnowledgeBase; 